<template>
  <div>
    <quill-editor
      :class="onlyText ? 'hide-icons' : ''"
      v-model.lazy="textMessage"
      :options="editorOption"
      :disabled="disabled"
      ref="editor"
      :key="editorKey"
    />
    <span  v-if="showCounter" class="float-right"> {{amountOfCharacters}}/{{maxLength}} </span>
  </div>
</template>

<script>
import 'quill/dist/quill.core.css'
import 'quill/dist/quill.snow.css'
import 'quill/dist/quill.bubble.css'
import 'quill-emoji/dist/quill-emoji.css'

import BlotFormatter, { DeleteAction, ImageSpec, ResizeAction } from 'quill-blot-formatter'
import { quillEditor } from 'vue-quill-editor'
import Quill from 'quill'

const Clipboard = Quill.import('modules/clipboard')
const Delta = Quill.import('delta')

class PreventCustomerPaste extends Clipboard {
  onPaste (e) {
    if (!e.clipboardData.getData('text/plain')) {
      e.preventDefault()
      const delta = new Delta().insert('')
      this.quill.updateContents(delta, 'silent')
    }
  }
}

Quill.register('modules/blotFormatter', BlotFormatter)
Quill.register('modules/maxLength', function (quill, options) {
  quill.on('text-change', (delta, oldDelta, source) => {
    if (quill.getLength() > options.value) {
      const content = quill.getContents(0, options.value - 1)
      quill.setContents(content)
    }
  })
})

class CustomImageSpec extends ImageSpec {
  getActions () {
    return [ResizeAction, DeleteAction]
  }
}

export default {
  components: {
    quillEditor
  },
  props: {
    customEvent: {
      type: Function,
      default: () => {}
    },
    value: String,
    disabled: Boolean,
    onlyText: {
      type: Boolean,
      default: false
    },
    isChannelTeams: {
      type: Boolean,
      default: false
    },
    maxLength: {
      type: Number
    },
    showCounter: {
      type: Boolean,
      default: () => false
    },
    theme: {
      type: String,
      default: 'snow'
    }
  },
  data () {
    return {
      amountOfCharacters: 0,
      editorKey: 1,
      editorOption: {
        theme: this.theme,
        placeholder: '',
        modules: {
          maxLength: {
            value: this.maxLength
          },
          clipboard: {
            matchers: [
              ['IMG', PreventCustomerPaste]
            ]
          },
          blotFormatter: {
            specs: [
              CustomImageSpec
            ]
          },
          'emoji-toolbar': true,
          'emoji-shortname': true,
          toolbar: {
            container: [
              'bold',
              'italic',
              'underline',
              'strike',
              { 'list': 'bullet' },
              'link',
              'emoji',
              'image'
            ]
          }
        }
      }
    }
  },
  computed: {
    textMessage: {
      get () {
        return this.value
      },
      set (value) {
        this.$emit('change', value)
        if (this.showCounter) {
          this.setAmountOfCharacters()
        }
      }
    }
  },
  created () {
    if (!this.maxLength) {
      delete this.editorOption.modules.maxLength
    }
    if (this.disabled) {
      delete this.editorOption.modules.blotFormatter
    }
    if (this.isChannelTeams) {
      this.addColorAndCleanToToolBar()
    }
    if (!this.onlyText) {
      delete this.editorOption.modules.clipboard
    } else {
      this.removeFormatting()
    }
  },
  mounted () {
    if (this.value) {
      this.setAmountOfCharacters()
    }
    if (this.disabled) {
      let toolbar = document.querySelector('.ql-formats')
      for (const child of toolbar.children) {
        child.setAttribute('disabled', true)
        child.classList.add('button-disabled')
      }
    }
  },
  methods: {
    removeDisabledConfigs () {
      if (this.disabled) {
        let toolbar = document.querySelector('.ql-formats')
        for (const child of toolbar.children) {
          child.removeAttribute('disabled')
          child.classList.remove('button-disabled')
        }
      }
    },
    removeColorAndCleanFromToolBar () {
      this.editorKey++
      let colorIndex = this.editorOption.modules.toolbar.container.findIndex(item => typeof item === 'object' && 'color' in item)
      let cleanIndex = this.editorOption.modules.toolbar.container.findIndex(item => item === 'clean')

      let newContainer = this.editorOption.modules.toolbar.container.filter((item, index) => index !== colorIndex && index !== cleanIndex)

      this.$set(this.editorOption.modules.toolbar, 'container', newContainer)
    },
    addColorAndCleanToToolBar () {
      this.editorKey++
      let baseContainerOptions = this.editorOption.modules.toolbar.container
      this.editorOption.modules.toolbar.container = [...baseContainerOptions, { 'color': ['#000000', '#646464', '#0063b1', '#028a02', '#dd9e00', '#ec130e'] }, 'clean']
    },
    removeFormatting () {
      this.$nextTick(() => {
        this.$refs.editor.quill.removeFormat(0, this.$refs.editor.quill.getLength(), 'silent')
      })
    },
    setAmountOfCharacters () {
      this.amountOfCharacters = this.$refs.editor.quill.getLength()
    }
  },
  watch: {
    isChannelTeams (newVal) {
      if (newVal) {
        this.addColorAndCleanToToolBar()
      } else {
        this.removeColorAndCleanFromToolBar()
      }
    }
  }
}
</script>

<style lang="scss" scoped>

.hide-icons {
  ::v-deep .ql-toolbar {
    border: 0.5px solid #949494;
    display: none;
  }
  ::v-deep .ql-editor {
    border-top: 1px solid #949494;
  }
}

::v-deep .ql-tooltip {
  left: 0 !important;
  z-index: 999;
}

::v-deep .ql-tooltip.ql-editing {
  left: 0 !important;
}

::v-deep .button-disabled {
  cursor: default !important;
}

::v-deep .ql-container {
  background-color: #F0F0F0;
  border: 0.5px solid #949494;
}

::v-deep strong {
  font-weight: 700;
}
</style>
